'use client';
import React, { useEffect, useRef, useState } from 'react';

import { Faq as FaqComponent } from '@monetization/hpaip-components-library';
import { env } from 'next-runtime-env';
import { useTranslation } from 'react-i18next';

import { FlowType } from '@storefront/app/flow-orchestrator/types';
import { getSkuList } from '@storefront/app/v2/pdp-config/printer/utils/utils';
import { useGetFaqsV2 } from '@storefront/hooks/useGetFaqsV2';
import { useIntersection } from '@storefront/hooks/useIntersection';
import { useProductCancellationList } from '@storefront/hooks/useProductCancellationList';
import { NamespacesI18n } from '@storefront/i18n';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { forwardParams } from '@storefront/utils/queryParamsUtils';

import { IFaqType } from './faq.utils';

interface FaqProps {
  searchParams: {
    skus: string;
    pdpFlow: FlowType;
  };
}

export function Faq({ searchParams }: Readonly<FaqProps>) {
  const [triggerAnalyticsOnce, setTriggerAnalyticsOnce] = useState(true);
  const [configuredFaqQuestion, setConfiguredFaqQuestion] = useState<
    IFaqType[]
  >([]);

  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const skus = getSkuList(searchParams.skus);
  const { cancellationList, multipleProduct, hasThreeYearsCancellationDate } =
    useProductCancellationList(skus);
  const ref = useRef(null);
  const inViewport = useIntersection(ref, '0px');

  const flowType = searchParams?.pdpFlow;

  const faqs = useGetFaqsV2({
    flowType,
    cancellationList,
    multipleProduct,
    skus,
    hasThreeYearsCancellationSku: hasThreeYearsCancellationDate,
  });

  useEffect(() => {
    if (configuredFaqQuestion.length <= 0 && faqs.length > 0) {
      setConfiguredFaqQuestion(faqs);
    }
  }, [faqs]);

  useEffect(() => {
    const productAnalytics = uiEvents.printerConfigFAQSD;
    if (inViewport && triggerAnalyticsOnce) {
      DataCollection.sendEvent(productAnalytics);
      setTriggerAnalyticsOnce(false);
    }
  }, [inViewport]);

  const handleExpand = (_?: unknown, index?: number) => {
    setConfiguredFaqQuestion((state) => {
      const updatedData = [...state];
      updatedData[index ?? 0].expanded = true;
      const productAnalytics = uiEvents.printerConfigFAQAccordianExpanded;
      DataCollection.sendEvent({
        ...productAnalytics,
        controlDetail: updatedData[index ?? 0].id,
      });
      return updatedData;
    });
  };

  const handleCollapse = (_?: unknown, index?: number) => {
    if (index !== undefined) {
      setConfiguredFaqQuestion((state) => {
        const updatedData = [...state];
        updatedData[index].expanded = false;
        return updatedData;
      });
    }
  };

  const handleClickFaqs = async () => {
    const faqUrl = env('NEXT_PUBLIC_FULL_FAQ_URL');
    const urlWithParams = forwardParams(faqUrl);

    const productAnalytics = uiEvents.printerConfigFAQBC;

    await DataCollection.sendEvent({
      ...productAnalytics,
      controlDetail: urlWithParams,
    });
    window.open(urlWithParams, '_self');
  };

  return (
    <FaqComponent
      title={t('faqV2.header')}
      handleClick={handleClickFaqs}
      onExpand={handleExpand}
      onCollapse={handleCollapse}
      items={configuredFaqQuestion}
      redirectText={t('faqV2.redirectText')}
    />
  );
}

export default Faq;
